import { read, remove, write } from '@/lib/storage'
import type { ExamRegistration } from '@/api/types'

/* Where a guest's token lives between page loads.

   The API client says, correctly, that an access token must never go to
   localStorage. This is the one deliberate exception in the app, and it is
   worth being explicit about why rather than letting it look like an oversight.

   A STUDENT keeps their token in memory only because they do not need it to
   survive: the httpOnly refresh cookie rebuilds the session on the next load.
   A GUEST has no such cookie — there is no credential behind a guest session
   to re-present, so the backend issues no refresh path and instead mints one
   long access token sized to the exam window. Hold that in memory only and a
   single reload mid-exam ends the attempt: no token, no way to get another
   without re-registering, and a paper the participant cannot get back into.
   On a phone, on mobile data, during a timed test, that is not an edge case.

   So the token is persisted, and the exposure is bounded on every side that
   can be bounded:

     · It is scoped to ONE share link. It cannot read a series, a dashboard, a
       profile or another test — the backend rejects a guest principal
       everywhere except that link's exam routes.
     · It expires at the window close plus a short grace, and is treated as
       gone here the moment `expires_at` passes, so a stale entry is never
       presented.
     · It is dropped as soon as the guest leaves, and `clearAll()` on sign-out
       already sweeps it along with the exam buffer.

   The answer buffer for the exam is already in localStorage next to it, so on
   a shared device this widens what is exposed by rather less than it first
   appears. */

const KEY = 'guest-session'

type StoredGuest = {
  access_token: string
  /** ISO-8601, straight off the register response. */
  expires_at: string
  /** The link this token is scoped to — used to spot a token for a different
   *  link still sitting in storage when a second link is opened. */
  share_link_token: string
  test_id: number
  medium: string
}

export function rememberGuestSession(registration: ExamRegistration): void {
  write<StoredGuest>(KEY, {
    access_token: registration.access_token,
    expires_at: registration.expires_at,
    share_link_token: registration.guest.share_link_token,
    test_id: registration.test_id,
    medium: registration.medium,
  })
}

/** The stored session, or null if there is none or it has expired. */
export function readGuestSession(): StoredGuest | null {
  const stored = read<StoredGuest>(KEY)
  if (stored === null || typeof stored.access_token !== 'string') return null

  const expiresAt = Date.parse(stored.expires_at)
  /* An unparseable date is treated as expired rather than as "no expiry": the
     failure mode of guessing wrong in the other direction is presenting a dead
     token on every request for the life of the browser profile. */
  if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
    forgetGuestSession()
    return null
  }
  return stored
}

/* Whether a guest entry is in storage AT ALL, expired or not.

   `readGuestSession` cannot answer this: it clears an expired entry and
   returns null, so "the window closed while they were away" and "this browser
   has never seen a share link" come back identical. Those two need different
   screens — one is a participant to explain the ending to, the other is a
   visitor with nothing to explain — so the caller checks this FIRST and then
   reads. It deliberately does not validate or clear anything; the read that
   follows does both. */
export function hasStoredGuestSession(): boolean {
  return read<StoredGuest>(KEY) !== null
}

export function forgetGuestSession(): void {
  remove(KEY)
}
